<template>
  <div :class="[isModeSubtitle ? 'multi-column' : 'one-column']">
    <div class="wrapper-features">
      <div class="wrapper-actions">
        <p class="small accent dim-dark">{{ t('label_language') }}</p>
        <ButtonDropdownLanguage v-model="preference.languageCode" chat-type="SCREEN" @select="selectLanguage" />
      </div>

      <div v-if="isModeSubtitle" class="wrapper-actions">
        <p class="small accent dim-dark">
          {{ t('subtitle_input_display_camera_label') }}
        </p>
        <div class="wrapper-buttons">
          <button class="button-gray-dim l-btn" @click="toggleCameraUser">
            {{ isCameraOnUser ? t('camera_turn_off') : t('camera_turn_on') }}
          </button>
          <button class="button-gray-dim l-btn" @click="startCameraDisplay">
            {{ t('subtitle_input_display_camera_label') }}
          </button>
        </div>
      </div>

      <div class="wrapper-radio">
        <SubtitleRadioBackgroundColor v-model="preference.subtitleBackgroundColor" />
        <SubtitleCheckboxLogo v-model="preference.subtitleIsVisibleLogo" />
      </div>

      <div :class="{ 'show-mobile-only': isModeSubtitle }" class="divider-small" />
    </div>

    <!--글씨조절-->
    <div class="wrapper-features">
      <p class="small accent dim-dark">{{ t('label_text_control') }}</p>
      <Gauge
        v-model="preference.textSizeMultiplier"
        v-bind="SUBTITLE_PREFERENCE_GAUGE_PRESET.textSizeMultiplier"
        :title="`${t('text_size')}: ${preference.textSizeMultiplier}`"
      />

      <Gauge
        v-model="preference.subtitleLineHeight"
        v-bind="SUBTITLE_PREFERENCE_GAUGE_PRESET.subtitleLineHeight"
        :title="`${t('label_line_height')}: ${preference.subtitleLineHeight}`"
      />

      <Gauge
        v-model="preference.subtitleLineCount"
        v-bind="SUBTITLE_PREFERENCE_GAUGE_PRESET.subtitleLineCount"
        :min-label="t(SUBTITLE_PREFERENCE_GAUGE_PRESET.subtitleLineCount.minLabel)"
        :max-label="t(SUBTITLE_PREFERENCE_GAUGE_PRESET.subtitleLineCount.maxLabel)"
        :title="`${t('label_line_count')}: ${preference.subtitleLineCount}`"
      />

      <div :class="{ 'show-mobile-only': isModeSubtitle }" class="divider-small" />
    </div>

    <!--자막영역조절-->
    <div class="wrapper-features">
      <p class="small accent dim-dark">{{ t('subtitle_area_control') }}</p>
      <Gauge
        v-model="preference.subtitleOpacity"
        v-bind="SUBTITLE_PREFERENCE_GAUGE_PRESET.subtitleOpacity"
        :title="`${t('subtitle_opacity')}: ${preference.subtitleOpacity}`"
      />

      <Gauge
        v-model="preference.subtitleLocationY"
        v-bind="SUBTITLE_PREFERENCE_GAUGE_PRESET.subtitleLocationY"
        :title="`${t('label_height')}: ${preference.subtitleLocationY}`"
      />

      <div class="wrapper-radio">
        <NumericInput
          v-model="preference.subtitleWidth"
          :title="t('subtitle_mode_message_width')"
          :min="validationRules.subtitleWidth.minimum"
          :max="validationRules.subtitleWidth.maximum"
          :validate="validationRules.subtitleWidth.validate"
          unit="%"
          using-button
        />
        <SubtitleCheckboxSettingPosition v-model="preference.subtitleIsSettingAbove" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ComputedRef, inject } from 'vue';
import { useI18n } from 'vue-i18n';

import SubtitleCheckboxLogo from '@/components/domains/chat/chat-subtitle/SubtitleCheckboxLogo.vue';
import SubtitleCheckboxSettingPosition from '@/components/domains/chat/chat-subtitle/SubtitleCheckboxSettingPosition.vue';
import SubtitleRadioBackgroundColor from '@/components/domains/chat/chat-subtitle/SubtitleRadioBackgroundColor.vue';
import ButtonDropdownLanguage from '@/components/domains/chat/common/ButtonDropdownLanguage.vue';
import Gauge from '@/components/shared/base/Gauge.vue';
import NumericInput from '@/components/shared/composite/NumericInput.vue';
import { useCamera } from '@/composables/shared/useCamera';
import { useChatPreferences } from '@/composables/shared/useChatPreferences';
import { useSocketStore } from '@/stores/socket';
import { LanguageCode } from '@/types/common';
import { ChatPreferenceStorageType, SubtitlePreferences } from '@/types/components';
import { getSubtitlePreference, SUBTITLE_PREFERENCE_GAUGE_PRESET } from '@/utils/constants/chatPreference';
import { ij_isModeSubtitle } from '@/utils/injectionKeys.js';

const preference = defineModel<SubtitlePreferences>({
  required: true,
  default: () => getSubtitlePreference(),
});

const props = withDefaults(
  defineProps<{
    type: ChatPreferenceStorageType;
  }>(),
  {
    type: 'remote',
  }
);

const isModeSubtitle = inject<ComputedRef>(
  ij_isModeSubtitle,
  computed(() => false)
);

const socketStore = useSocketStore();

const { t } = useI18n();

const { isCameraOnUser, startCamera, stopCamera } = useCamera();
const { validationRules } = useChatPreferences();

const startCameraUser = () => {
  startCamera('user');
};

const startCameraDisplay = () => {
  startCamera('display');
};

const toggleCameraUser = () => {
  if (isCameraOnUser.value) {
    stopCamera();
  } else {
    startCameraUser();
  }
};

const selectLanguage = (languageCode: LanguageCode) => {
  if (props.type === 'local') {
    socketStore.sendMessageChangeLanguage(languageCode);
  }
};
</script>

<style scoped lang="scss">
.multi-column {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3rem;

  @include respond-to(mobile-landscape) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

.one-column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.wrapper-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  border-radius: 14px;
}

.wrapper-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.wrapper-buttons {
  display: flex;
  gap: 1rem;
}

.wrapper-radio {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: $spacing3;
}

button {
  max-height: 3rem;
}
</style>
